import { media } from '@/styles/Qtheme';
import styled, { keyframes } from 'styled-components';

// 모달 페이드인 + 스케일 애니메이션
const modalFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

// 오버레이 페이드 인
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// 오버레이 (배경 어둡게)
export const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 999;
  animation: ${fadeIn} 0.2s ease-out;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

// 모달 컨테이너 (중앙 정렬)
export const ModalContainer = styled.div<{
  isDarkmode: boolean;
  isOpen: boolean;
}>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  background-color: ${({ isDarkmode }) => (isDarkmode ? '#1A1A1A' : '#FFFFFF')};
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  overflow-y: auto;
  animation: ${modalFadeIn} 0.3s ease-out;
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;

  @media ${media.mobile} {
    width: 95%;
    max-height: 90vh;
  }

  /* 스크롤바 스타일 */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ isDarkmode }) => (isDarkmode ? '#2A2A2A' : '#F0F0F0')};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ isDarkmode }) => (isDarkmode ? '#444444' : '#CCCCCC')};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ isDarkmode }) => (isDarkmode ? '#555555' : '#AAAAAA')};
  }
`;

// 헤더
export const Header = styled.div<{ isDarkmode: boolean }>`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid
    ${({ isDarkmode }) => (isDarkmode ? '#333' : '#E0E0E0')};
  background-color: inherit;
  z-index: 10;

  @media ${media.mobile} {
    padding: 20px 20px;
  }
`;

export const HeaderTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0;

  @media ${media.mobile} {
    font-size: 18px;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

// Tier 캐러셀 섹션
export const TierCarouselSection = styled.div`
  position: relative;
  padding: 28px;

  @media ${media.mobile} {
    padding: 20px;
  }
`;

// 화살표 버튼
export const ArrowButton = styled.button<{
  direction: 'left' | 'right';
  isDarkmode: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ direction }) => (direction === 'left' ? 'left: 8px;' : 'right: 8px;')}

  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ isDarkmode }) => (isDarkmode ? '#1f1f1f' : '#F0F0F0')};
  border: 2px solid
    ${({ isDarkmode }) =>
      isDarkmode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
  color: ${({ isDarkmode }) => (isDarkmode ? '#FFF' : '#333')};

  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  transition: all 0.2s ease;

  &:hover {
    background-color: #ff5c17;
    border-color: #ff5c17;
    color: #fff;
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  @media ${media.mobile} {
    width: 36px;
    height: 36px;
    ${({ direction }) => (direction === 'left' ? 'left: 4px;' : 'right: 4px;')}
  }
`;

// Tier 카드 래퍼 (가로 스크롤, 스크롤바 숨김)
export const TierCardWrapper = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: hidden; /* 스크롤바 완전히 숨김 */
  padding: 4px; /* 상하좌우 패딩 */
  padding-right: 8px; /* 마지막 카드 오른쪽 테두리 안 잘리게 추가 여백 */
  margin: 0 -4px; /* 패딩 추가로 인한 위치 조정 */
  scroll-behavior: smooth;

  @media ${media.mobile} {
    gap: 16px;
    padding: 4px;
    padding-right: 8px;
  }
`;

// Tier 카드 (2개씩 보이도록 크기 조정)
export const TierCard = styled.div<{
  isDarkmode: boolean;
  isAchieved: boolean;
}>`
  min-width: calc(50% - 10px); /* 2개씩, 간격 20px 고려 */
  max-width: calc(50% - 10px);
  flex-shrink: 0;
  background-color: ${({ isDarkmode, isAchieved }) =>
    isAchieved
      ? isDarkmode
        ? '#2A3A2A'
        : '#F0FAF0'
      : isDarkmode
        ? '#252525'
        : '#FFFFFF'};
  border: 2px solid
    ${({ isDarkmode, isAchieved }) =>
      isAchieved ? '#4CAF50' : isDarkmode ? '#3A3A3A' : '#E0E0E0'};
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.3s ease;
  scroll-snap-align: start;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }

  @media ${media.mobile} {
    min-width: 100%; /* 모바일에서는 1개씩 */
    max-width: 100%;
    padding: 20px;
    gap: 16px;
  }
`;

// Tier 배지
export const TierBadge = styled.div<{ isAchieved: boolean }>`
  background-color: ${({ isAchieved }) => (isAchieved ? '#4CAF50' : '#FF5C17')};
  color: #ffffff;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media ${media.mobile} {
    font-size: 13px;
    padding: 6px 14px;
  }
`;

// 목표 섹션
export const GoalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionTitle = styled.div<{ isDarkmode: boolean }>`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  opacity: 0.6;
  letter-spacing: 0.5px;
  margin-bottom: 4px;

  @media ${media.mobile} {
    font-size: 11px;
  }
`;

export const GoalItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const GoalLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  opacity: 0.7;

  @media ${media.mobile} {
    font-size: 12px;
  }
`;

export const GoalValue = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #ff5c17;

  @media ${media.mobile} {
    font-size: 16px;
  }
`;

// 보상 섹션
export const RewardSection = styled.div<{ isDarkmode: boolean }>`
  background-color: ${({ isDarkmode }) =>
    isDarkmode ? 'rgba(255, 92, 23, 0.12)' : 'rgba(255, 92, 23, 0.08)'};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media ${media.mobile} {
    padding: 14px;
  }
`;

export const RewardLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media ${media.mobile} {
    font-size: 11px;
  }
`;

export const RewardAmount = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #ff5c17;

  @media ${media.mobile} {
    font-size: 20px;
  }
`;

// 버튼 섹션
export const ButtonSection = styled.div`
  margin-top: auto;
`;

// 수령완료 배지
export const ClaimedBadge = styled.div<{ isDarkmode: boolean }>`
  width: 100%;
  padding: 14px 16px;
  border-radius: 10px;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  background-color: ${({ isDarkmode }) => (isDarkmode ? '#2A4A2A' : '#E8F5E9')};
  color: #4caf50;
  border: 2px solid #4caf50;

  @media ${media.mobile} {
    padding: 12px 14px;
    font-size: 14px;
  }
`;

// 미달성 배지
export const NotAchievedBadge = styled.div<{ isDarkmode: boolean }>`
  width: 100%;
  padding: 14px 16px;
  border-radius: 10px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  background-color: ${({ isDarkmode }) => (isDarkmode ? '#3A3A3A' : '#F5F5F5')};
  color: ${({ isDarkmode }) => (isDarkmode ? '#888' : '#999')};
  border: 2px solid ${({ isDarkmode }) => (isDarkmode ? '#555' : '#DDD')};

  @media ${media.mobile} {
    padding: 12px 14px;
    font-size: 14px;
  }
`;
